import React,{useState}from'react';
import{useNavigate}from'react-router-dom';
import{useTranslation}from'react-i18next';
import api from'../services/api';
import{useDispatch}from'react-redux';
import{setCredentials}from'../store/authSlice';
import{SUPPORTED_LANGUAGES}from'../i18n';

export default function LoginPage(){
  const nav=useNavigate();
  const dispatch=useDispatch();
  const {t,i18n}=useTranslation();
  const [step,setStep]=useState<1|2|3>(1);
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [mfaToken,setMfaToken]=useState('');
  const [otp,setOtp]=useState('');
  const [branches,setBranches]=useState<any[]>([]);
  const [stations,setStations]=useState<any[]>([]);
  const [branchId,setBranchId]=useState('');
  const [stationId,setStationId]=useState('');
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [showPwd,setShowPwd]=useState(false);
  const [showForgot,setShowForgot]=useState(false);
  const [forgotEmail,setForgotEmail]=useState('');
  const [forgotMsg,setForgotMsg]=useState('');
  const [forgotLoading,setForgotLoading]=useState(false);

  const submitForgotPassword=async()=>{
    if(!forgotEmail){setForgotMsg(t('login.enterEmailAddress','Enter your email address'));return;}
    setForgotLoading(true);setForgotMsg('');
    try{
      const r=await api.forgotPassword({email:forgotEmail});
      setForgotMsg(r?.data?.message||t('login.resetLinkSent','If this email is registered, a reset link has been sent.'));
    }catch{
      setForgotMsg(t('login.resetLinkSent','If this email is registered, a reset link has been sent.'));
    }
    setForgotLoading(false);
  };

  const login=async()=>{
    if(!email||!password){setError(t('login.emailPasswordRequired','Email and password required'));return;}
    setLoading(true);setError('');
    try{
      const r=await api.login({email,password});
      if(r?.data?.mfaRequired){
        setMfaToken(r.data.mfaToken||'');
        setOtp('');
        setStep(2);
        setLoading(false);
        return;
      }
      const token=r?.data?.access_token||r?.data?.token||r?.data?.accessToken;
      const user=r?.data?.user||r?.data;
      if(!token){setError(t('login.invalidCredentials','Invalid credentials'));setLoading(false);return;}
      localStorage.setItem('accessToken',token);
      dispatch(setCredentials({user,token}));
      localStorage.setItem('userName',user?.first_name?`${user.first_name} ${user.last_name}`:user?.name||email);
      localStorage.setItem('userRole',user?.role||user?.roles?.[0]||'STAFF');
      localStorage.setItem('userId',user?.id||user?.sub||'');
      if(user?.mustChangePassword){nav('/change-password');setLoading(false);return;}
      // Load branches
      const br=await api.getMyBranches?.().catch(()=>({data:[]}));
      const brs=Array.isArray(br?.data)?br.data:br?.data?.data||[];
      setBranches(brs);
      setStep(3);
    }catch(e:any){setError(e.response?.data?.message||t('login.loginFailed','Login failed. Check credentials.'));}
    setLoading(false);
  };

  const verifyMfa=async()=>{
    if(!/^\d{6}$/.test(otp)){setError(t('login.mfaCodeRequired','Enter the 6-digit authenticator code'));return;}
    setLoading(true);setError('');
    try{
      const r=await api.verifyLoginMfa({mfaToken,code:otp});
      const token=r?.data?.access_token||r?.data?.token||r?.data?.accessToken;
      const user=r?.data?.user||r?.data;
      if(!token){setError(t('login.invalidMfa','Invalid authentication code'));setLoading(false);return;}
      localStorage.setItem('accessToken',token);
      dispatch(setCredentials({user,token}));
      localStorage.setItem('userName',user?.firstName?`${user.firstName} ${user.lastName}`:user?.first_name?`${user.first_name} ${user.last_name}`:user?.name||email);
      localStorage.setItem('userRole',user?.role||user?.roles?.[0]||'STAFF');
      localStorage.setItem('userId',user?.id||user?.sub||'');
      if(user?.mustChangePassword){nav('/change-password');setLoading(false);return;}
      const br=await api.getMyBranches?.().catch(()=>({data:[]}));
      const brs=Array.isArray(br?.data)?br.data:br?.data?.data||[];
      setBranches(brs);
      setStep(3);
    }catch(e:any){setError(e.response?.data?.message||t('login.invalidMfa','Invalid authentication code'));}
    setLoading(false);
  };

  const loadStations=async(bid:string)=>{
    setBranchId(bid);setStationId('');
    try{
      const r=await api.getWorkstations?.(bid).catch(()=>({data:[]}));
      let stns=Array.isArray(r?.data)?r.data:r?.data?.data||[];
      if(stns.length===0){
        await api.seedDefaultBranch?.({branchId:bid}).catch(()=>{});
        const r2=await api.getWorkstations?.(bid).catch(()=>({data:[]}));
        stns=Array.isArray(r2?.data)?r2.data:r2?.data?.data||[];
      }
      setStations(stns);
    }catch{}
  };

  const enter=()=>{
    if(!branchId||!stationId){setError(t('login.selectBranchWorkstation','Select branch and workstation'));return;}
    const branch=branches.find((b:any)=>b.id===branchId);
    const station=stations.find((s:any)=>s.id===stationId);
    localStorage.setItem('branchId',branchId);
    localStorage.setItem('branchName',branch?.branch_name||branch?.name||'Hospital');
    localStorage.setItem('stationId',stationId);
    localStorage.setItem('stationName',station?.station_name||station?.name||'Reception');
    localStorage.setItem('activeBranchId',branchId);
    nav('/dashboard');
  };

  return(
    <div className="min-h-screen flex" style={{background:'linear-gradient(135deg, #02357A 0%, #015CA6 50%, #039089 100%)'}}>
      {/* Language switcher — visible before any login, since a first-time visitor has no prior
          session for i18next-browser-languagedetector to have cached a choice from. */}
      <select
        aria-label="Language"
        className="absolute top-4 right-4 text-xs text-white bg-white/10 border border-white/30 rounded-md px-2 py-1 focus:outline-none z-10"
        value={i18n.language?.split('-')[0]||'en'}
        onChange={e=>i18n.changeLanguage(e.target.value)}
      >
        {SUPPORTED_LANGUAGES.map(l=><option key={l.code} value={l.code} className="text-gray-800">{l.label}</option>)}
      </select>

      {/* ── Left Panel ── */}
      <div className="hidden lg:flex flex-col justify-center items-center w-1/2 p-12 text-white">
        <img src="/logo.png" alt="Clinqov Meditrack" className="w-36 h-36 object-contain mb-8 drop-shadow-2xl"/>
        <h1 className="text-4xl font-bold text-center leading-tight">Clinqov Meditrack</h1>
        <p className="text-lg mt-2 text-white/70 text-center">Private Limited</p>
        <div className="w-16 h-0.5 bg-white/30 my-6"/>
        <p className="text-white/70 text-center text-sm leading-relaxed max-w-xs">
          {t('login.tagline','Enterprise Hospital Information System — Powered by advanced clinical workflows and SHA (Social Health Authority) integration.')}
        </p>
        <div className="mt-10 grid grid-cols-2 gap-4 w-full max-w-xs">
          {[['724','Database Tables'],['40+','Clinical Modules'],['76','App Pages'],['1,276','API Routes']].map(([v,l])=>(
            <div key={l} className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm border border-white/20">
              <p className="text-2xl font-bold text-white">{v}</p>
              <p className="text-xs text-white/60 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            
            {/* Card header */}
            <div className="px-8 pt-8 pb-6" style={{background:'linear-gradient(135deg, #02357A, #015CA6)'}}>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="CM" className="w-10 h-10 rounded-lg object-contain bg-white p-1"/>
                <div>
                  <p className="text-white font-bold text-base leading-tight">Clinqov Meditrack</p>
                  <p className="text-white/60 text-xs">{t('login.hospitalInformationSystem','Hospital Information System')}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {(mfaToken?[1,2,3]:[1,2]).map(n=>(
                  <div key={n} className={`h-1 flex-1 rounded-full ${(mfaToken?step:step===3?2:step)>=n?'bg-white':'bg-white/30'}`}/>
                ))}
              </div>
              <p className="text-white/70 text-xs mt-2">
                {t('login.stepOf','Step {{step}} of {{total}} — {{stage}}',{
                  step:mfaToken?step:step===3?2:step,
                  total:mfaToken?3:2,
                  stage:step===1?t('login.authentication','Authentication'):step===2?t('login.twoFactor','Two-factor authentication'):t('login.selectLocation','Select Location'),
                })}
              </p>
            </div>

            {/* Card body */}
            <div className="px-8 py-7">
              {error&&(
                <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-2">
                  <span>⚠️</span>{error}
                </div>
              )}

              {step===1&&(
                <>
                  <h2 className="text-xl font-bold text-gray-800 mb-1">{t('login.welcomeBack','Welcome back')}</h2>
                  <p className="text-gray-500 text-sm mb-6">{t('login.signInToContinue','Sign in to continue to your workspace')}</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('login.emailAddress','Email Address')}</label>
                      <input value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&login()}
                        type="email" placeholder="doctor@hospital.com" autoFocus
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none transition-all"
                        style={{'--tw-ring-color':'#015CA6'} as any}/>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('login.password','Password')}</label>
                      <div className="relative">
                        <input value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&login()}
                          type={showPwd?'text':'password'} placeholder="Enter password"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none"/>
                        <button type="button" onClick={()=>setShowPwd(s=>!s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">
                          {showPwd?'🙈':'👁️'}
                        </button>
                      </div>
                      <button type="button" onClick={()=>{setShowForgot(s=>!s);setForgotMsg('');}} className="text-xs text-[#015CA6] hover:underline mt-1.5">
                        {t('login.forgotPassword','Forgot password?')}
                      </button>
                    </div>
                  </div>
                  {showForgot&&(
                    <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                      <p className="text-xs text-gray-500 mb-2">{t('login.forgotPasswordHint',"Enter your email and we'll send you a reset link.")}</p>
                      <div className="flex gap-2">
                        <input value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&submitForgotPassword()}
                          type="email" placeholder="your@email.com"
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/>
                        <button onClick={submitForgotPassword} disabled={forgotLoading}
                          className="px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                          style={{background:'#039089'}}>
                          {forgotLoading?t('login.sending','Sending…'):t('login.send','Send')}
                        </button>
                      </div>
                      {forgotMsg&&<p className="text-xs text-green-700 mt-2">{forgotMsg}</p>}
                    </div>
                  )}
                  <button onClick={login} disabled={loading}
                    className="w-full mt-6 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition-all hover:opacity-90 active:scale-95"
                    style={{background:'linear-gradient(135deg, #02357A, #015CA6)'}}>
                    {loading?t('login.signingIn','Signing in…'):t('login.signIn','Sign In →')}
                  </button>
                  <p className="text-center text-xs text-gray-400 mt-4">
                    {t('login.defaultPasswordPrefix','Default:')} <span className="font-mono text-gray-600">password</span> {t('login.defaultPasswordSuffix','for all seeded accounts')}
                  </p>
                </>
              )}

              {step===2&&(
                <>
                  <h2 className="text-xl font-bold text-gray-800 mb-1">{t('login.twoFactor','Two-factor authentication')}</h2>
                  <p className="text-gray-500 text-sm mb-6">{t('login.mfaHint','Enter the 6-digit code from your authenticator app for Clinqov Meditrack.')}</p>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('login.authenticatorCode','Authenticator code')}</label>
                    <input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,'').slice(0,6))} onKeyDown={e=>e.key==='Enter'&&verifyMfa()}
                      inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="000000" maxLength={6}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm tracking-[0.4em] font-mono focus:outline-none"/>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={()=>{setStep(1);setMfaToken('');setOtp('');setError('');}} className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-medium text-sm hover:border-gray-300 transition-all">
                      {t('login.back','← Back')}
                    </button>
                    <button onClick={verifyMfa} disabled={loading||otp.length!==6}
                      className="flex-1 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-40 transition-all hover:opacity-90 active:scale-95"
                      style={{background:'linear-gradient(135deg, #02357A, #015CA6)'}}>
                      {loading?t('login.verifying','Verifying…'):t('login.verifyCode','Verify code →')}
                    </button>
                  </div>
                </>
              )}

              {step===3&&(
                <>
                  <h2 className="text-xl font-bold text-gray-800 mb-1">{t('login.selectLocation','Select Location')}</h2>
                  <p className="text-gray-500 text-sm mb-6">{t('login.chooseBranchStation','Choose your working branch and station')}</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('login.branch','Branch')}</label>
                      {branches.length===0?(
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                          {t('login.noBranchesAvailable','No branches available. Contact admin.')}
                        </div>
                      ):(
                        <div className="grid gap-2">
                          {branches.map((b:any)=>(
                            <button key={b.id} onClick={()=>loadStations(b.id)}
                              className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm ${
                                branchId===b.id?'border-[#015CA6] bg-blue-50 text-[#02357A] font-semibold':'border-gray-100 hover:border-gray-300 text-gray-700'}`}>
                              <div className="flex items-center gap-2">
                                <span>{b.branch_type==='LAB'?'🧪':b.branch_type==='PHARMACY'?'💊':'🏥'}</span>
                                <div>
                                  <p className="font-medium">{b.branch_name||b.name}</p>
                                  <p className="text-xs text-gray-400">{b.branch_code||''}</p>
                                </div>
                                {branchId===b.id&&<span className="ml-auto text-[#039089]">✓</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {branchId&&stations.length>0&&(
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('login.workstation','Workstation')}</label>
                        <select value={stationId} onChange={e=>setStationId(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white focus:outline-none">
                          <option value="">{t('login.selectWorkstation','Select workstation')}</option>
                          {stations.map((s:any)=>(
                            <option key={s.id} value={s.id}>{s.station_name||s.name} ({s.station_type||s.type||'General'})</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={()=>setStep(1)} className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-medium text-sm hover:border-gray-300 transition-all">
                      {t('login.back','← Back')}
                    </button>
                    <button onClick={enter} disabled={!branchId||!stationId}
                      className="flex-2 flex-1 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-40 transition-all hover:opacity-90 active:scale-95"
                      style={{background:'linear-gradient(135deg, #039089, #027070)'}}>
                      {t('login.enterSystem','Enter System →')}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 pb-6 text-center">
              <p className="text-xs text-gray-400">© 2025 Clinqov Meditrack Pvt. Ltd. · SHA Ready</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
